import { useDispatch, useSelector } from 'react-redux';
import { setErrorCode, setErrorMessage, setImageErrorCode, setImageErrorMessage, setIsLoadingMain, setPortraitUUID, setStep } from '../store/sharedStateSlice';
import i18n from '../i18n';
import { BASE_URL, CONFIG, ENVIRONMENT_NAME } from '../config';
import { useAuth } from '../contexts/AuthContext';

function useCustomFetchApi(showNotification = () => {}) {
  const baseUrl = BASE_URL + "api";
  const { step, portraitUUID, loading, STEPS } = useSelector(state => state.sharedState);
  const dispatch = useDispatch();
  const { token } = useAuth();

  async function request(endpoint, options = {}) {
    return await requestInternal(endpoint, options);
  }

  async function requestInternal(endpoint, options = {}) {
    if (options.isLoadingMain) {
      dispatch(setIsLoadingMain(true));
    }

    const url = `${baseUrl}${endpoint}`;

    // Determine language from URL segment if valid, else fall back to i18n.language
    let lang = i18n?.language || 'en';
    try {
      const firstSegment = (window.location.pathname || '').split('/')[1] || '';
      const availableLangs = Object.keys(i18n?.options?.resources || {});
      if (firstSegment && availableLangs.includes(firstSegment)) {
        lang = firstSegment;
      }
    } catch (_) { /* noop */ }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': lang,
        'X-Environment': ENVIRONMENT_NAME,
        'X-Client-Version': process.env.REACT_APP_VERSION || '1.0.0',
        ...(options.headers || {}),
      },
      credentials: 'include',
      timeout: CONFIG.api.timeout,
      ...options,
    };

    // Add Bearer token for authenticated requests
    if (token && !options.skipAuth) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // No CSRF token handling

    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Optional: attach debug info to document for non-console diagnostics
    // if (CONFIG.features.debug && typeof document !== 'undefined') {
    //   document.documentElement.setAttribute('data-last-api', url);
    // }

    try {
      const response = await fetch(url, config);
      
      // Handle different response types
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }

      // No console logging in production builds
      if (data.step) await dispatch(setStep(data.step))
      if (data.portraitUUID) await dispatch(setPortraitUUID(data.portraitUUID))
      if (data.code) await dispatch(setErrorCode(data.code))
      if (data.error) await dispatch(setErrorMessage(data.error))
      if (data.imageErrorMessage) await dispatch(setImageErrorMessage(data.imageErrorMessage))
      if (data.imageErrorCode) await dispatch(setImageErrorCode(data.imageErrorCode))

      if (!response.ok) {
//        console.log('Response not ok:', response.status, response.statusText, data);

        // Public endpoints that should not force redirect on 401/Unauthorized
        const publicUnauthAllowed = [
          '/list',
          '/list/public',
          '/countries',
          '/configs',
          '/session-state'
        ];
        const isPublicAllowed = publicUnauthAllowed.some(p => endpoint.startsWith(p));

        // Only redirect if this request explicitly requires auth AND server says Unauthorized
        const requireAuth = options.requireAuth === true; // caller can force auth

        if ((response.status === 401 || response.status === 403) && data?.error === 'Unauthorized') {
          if (requireAuth && !isPublicAllowed) {
//            console.log('Unauthorized on protected endpoint, redirecting to login.');
            window.location.href = '/login';
            return;
          } else {
            // Suppress redirect & error notification for public endpoints; return empty object
            return {};
          }
        }

        let errorMessage = 'Request failed';

        if (data.error) {
          errorMessage = data.error;
          const fullError = new Error(errorMessage);
          fullError.status = response.status;
          fullError.environment = ENVIRONMENT_NAME;

          if (options.showNotification !== false) {
            showNotification(errorMessage, 'error');
          }

          throw fullError;
        }
      }

      return data;
    } catch (error) {
      // Enhanced error handling for different environments
      if (CONFIG.features.debug) {
        console.error(`[${ENVIRONMENT_NAME.toUpperCase()}] API Error:`, {
          url,
          error: error.message,
          status: error.status
        });
      }

      // Environment-specific error handling
      if (ENVIRONMENT_NAME === 'local' && error.name === 'TypeError') {
        const localError = new Error('Local server connection failed. Make sure the backend is running.');
        if (options.showNotification !== false) {
          showNotification('Local server connection failed', 'error');
        }
        throw localError;
      }

      throw error;
    } finally {
      if (options.isLoadingMain) {
        dispatch(setIsLoadingMain(false));
      }
    }
  }

  // Retry logic for beta and prod environments
  async function requestWithRetry(endpoint, options = {}, retries = CONFIG.api.retries) {
    for (let i = 0; i <= retries; i++) {
      try {
        return await requestInternal(endpoint, options);
      } catch (error) {
        const isLastAttempt = i === retries;
        const shouldRetry = !isLastAttempt && 
                           ENVIRONMENT_NAME !== 'local' && 
                           (error.status >= 500 || error.name === 'TypeError');

        if (shouldRetry) {
          const delay = Math.min(1000 * Math.pow(2, i), 5000); // Exponential backoff
          // No console logging
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        throw error;
      }
    }
  }


  async function get(endpoint, options = {}) {
    const requestFn = ENVIRONMENT_NAME === 'local' ? request : requestWithRetry;
    return requestFn(endpoint, { method: 'GET', ...options });
  }

  async function post(endpoint, data = null, options = {}) {
    const config = {
      method: 'POST',
      ...options,
    };

    if (data && !(data instanceof FormData)) {
      config.body = JSON.stringify(data);
    } else if (data instanceof FormData) {
      config.body = data;
    }

    const requestFn = ENVIRONMENT_NAME === 'local' ? request : requestWithRetry;
    return requestFn(endpoint, config);
  }

  async function put(endpoint, data = null, options = {}) {
    const config = {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      ...options
    };

    const requestFn = ENVIRONMENT_NAME === 'local' ? request : requestWithRetry;
    return requestFn(endpoint, config);
  }

  async function del(endpoint, options = {}) {
    const requestFn = ENVIRONMENT_NAME === 'local' ? request : requestWithRetry;
    return requestFn(endpoint, { method: 'DELETE', ...options });
  }

  async function requestWithSuccess(endpoint, options = {}, successMessage = null) {
    const requestFn = ENVIRONMENT_NAME === 'local' ? request : requestWithRetry;
    const result = await requestFn(endpoint, options);
    if (successMessage) {
      showNotification(successMessage, 'success');
    }
    return result;
  }

  async function postWithSuccess(endpoint, data = null, successMessage = null, options = {}) {
    const result = await post(endpoint, data, options);
    if (successMessage) {
      showNotification(successMessage, 'success');
    }
    return result;
  }

  return {
    get,
    post,
    put,
    del,
    request,
    requestWithRetry,
    requestWithSuccess,
    postWithSuccess,
    step,
    portraitUUID,
    loading,
    STEPS,
    // Environment info for debugging
    environment: ENVIRONMENT_NAME,
    apiUrl: baseUrl,
    config: CONFIG
  };
}

export default useCustomFetchApi;
